import type { PermissionName, ZgriPermissionName } from '@/utils/types/Permission';

import { useUser } from '@/hooks';
import { hasUserPermission, permissionIsNotSet } from '@/utils/permissions.util';
import { UserPermissions } from '@/constants/permissions.constant';

type UserPermission = {
  notSet: boolean;
  enabled: boolean;
};

export const usePermission = (permissionName: PermissionName): UserPermission => {
  const { isPublic, user } = useUser();

  if (isPublic) return { notSet: false, enabled: false };

  if (!user?.userPermissions) return { notSet: false, enabled: false };

  const isPermissionNotSet = permissionIsNotSet(permissionName, user.userPermissions);

  if (isPermissionNotSet) return { notSet: true, enabled: false };

  return { notSet: false, enabled: hasUserPermission(permissionName, user.userPermissions) };
};

export const useZgriPermission = (permissionName: ZgriPermissionName): UserPermission => {
  return usePermission(permissionName);
};

export const useZgriDashboardViewPermission = (): UserPermission => {
  const { enabled: zgriUseLandlordFeaturesEnabled, notSet: zgriUseLandlordFeaturesNotSet } =
    useZgriPermission(UserPermissions.Zgri.UseLandlordFeatures);

  const { enabled: zgriUseOpsFeaturesEnabled, notSet: zgriUseOpsFeaturesNotSet } =
    useZgriPermission(UserPermissions.Zgri.UseOpsFeatures);

  const enabled = zgriUseLandlordFeaturesEnabled || zgriUseOpsFeaturesEnabled;
  const notSet = zgriUseLandlordFeaturesNotSet && zgriUseOpsFeaturesNotSet;

  return { notSet, enabled };
};

export const useZgriOpsPermission = (): boolean => {
  const { enabled, notSet } = useZgriPermission(UserPermissions.Zgri.UseOpsFeatures);

  if (notSet) {
    return false;
  }

  if (!enabled) {
    return false;
  }

  return true;
};

export const useZgriLandlordPermission = (): boolean => {
  const { enabled, notSet } = useZgriPermission(UserPermissions.Zgri.UseLandlordFeatures);

  if (notSet) {
    return false;
  }

  if (!enabled) {
    return false;
  }

  return true;
};
